import React, { useEffect, useRef, useState } from 'react'
import { RenderPlayerParams } from '../../content/Video/player-render-params'
import { StyledWrapper, StyledVideo } from '../../content/Video/Video.styled'
import { VerticalVideoWrapper } from './VerticalVideoSkin.styled'
import { UnmuteButton } from '../../content/Video/components/UnmuteButton/UnmuteButton'
import { PlayOverlay } from '../../content/Video/components/PlayOverlay/PlayOverlay'

export const VerticalVideoSkin: React.FC<RenderPlayerParams> = ({
    theme,
    wrapperElProps,
    videoElement,
    playerState,
    playerOptions,
    playerActions,
    hideSticky,
}) => {
    const [isPaused, setIsPaused] = useState(false)
    const [isVideoReady, setIsVideoReady] = useState(false)
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const videoRef = useRef<HTMLVideoElement | null>(null)

    useEffect(() => {
        setIsPaused(false)
        setIsVideoReady(false)
    }, [playerState.currentVideo.id])

    useEffect(() => {
        const wrapper = wrapperRef.current
        const video = wrapper?.querySelector('video') as HTMLVideoElement | null
        if (!video) return

        videoRef.current = video
        let isActive = true

        const handlePlay = () => {
            if (!isActive) return
            setIsPaused(false)
        }
        const handlePlaying = () => {
            if (!isActive) return
            setIsPaused(false)
        }
        const handlePause = () => {
            if (!isActive) return
            setIsPaused(true)
        }
        const handleLoaded = () => {
            if (!isActive) return
            setIsVideoReady(true)
            setIsPaused(video.paused)
        }

        if (video.readyState > 0) {
            setIsVideoReady(true)
            setIsPaused(video.paused)
        }
        video.addEventListener('play', handlePlay)
        video.addEventListener('playing', handlePlaying)
        video.addEventListener('pause', handlePause)
        video.addEventListener('loadedmetadata', handleLoaded)

        return () => {
            isActive = false
            video.removeEventListener('play', handlePlay)
            video.removeEventListener('playing', handlePlaying)
            video.removeEventListener('pause', handlePause)
            video.removeEventListener('loadedmetadata', handleLoaded)
        }
    }, [playerState.currentVideo.id, videoElement])

    const handleMuteClick = () => {
        playerActions.handleMuteToggle()
    }

    const handleTogglePlay = () => {
        if (playerState.adPlaying) {
            return
        }
        const wrapper = wrapperRef.current
        const video = wrapper?.querySelector('video') as HTMLVideoElement | null
        if (!video) {
            return
        }

        if (video.paused) {
            setIsPaused(false)
            playerActions.playVideo()
            video.play().catch(() => {})
        } else {
            playerActions.pauseVideo()
            video.pause()
            setIsPaused(true)
        }
    }

    const handlePlayOverlay = () => {
        return
    }

    const renderUnmuteButton = !playerState.showTitleOverlay ? (
        <UnmuteButton
            variant="vertical"
            positionState={{ alignToTop: true }}
            isMuted={playerState.isMuted}
            action={handleMuteClick}
        />
    ) : null

    const { innerRef, onTouchEnd, ...restWrapperElProps } = wrapperElProps
    const setWrapperRef = (el: HTMLElement | null) => {
        innerRef(el)
        wrapperRef.current = el as HTMLDivElement | null
    }

    return (
        <StyledWrapper
            {...restWrapperElProps}
            compactMode={playerOptions.compactMode}
            hide={hideSticky && playerOptions.compactMode}
            onTouchEnd={onTouchEnd}
            ref={setWrapperRef}
        >
            <VerticalVideoWrapper>
                <StyledVideo>
                    <div
                        className="VerticalVideoTapOverlay"
                        onClick={handleTogglePlay}
                        style={{
                            pointerEvents: playerState.adPlaying
                                ? 'none'
                                : 'auto',
                        }}
                    >
                        {isPaused &&
                            isVideoReady &&
                            playerState.currentVideoMetadata && (
                            <PlayOverlay
                                play={handlePlayOverlay}
                                isCentered={true}
                                isSmall={playerOptions.smallPlayButton}
                            />
                        )}
                    </div>
                    {videoElement}
                    {renderUnmuteButton}
                </StyledVideo>
            </VerticalVideoWrapper>
        </StyledWrapper>
    )
}
